import { TSCard } from '@/components/shared/card/Card';
import { Tooltip } from '@/components/tooltip/Tooltip';
import { BASE_TOOLTIP_CLASSES } from '@/components/tooltip/tooltip.classes';
import {
  OptimisationEvaluationConstraintResult,
  OptimisationEvaluationWithSummary,
  useOptimisationEvaluationHistory
} from '@/services/optimiser/useOptimisationEvaluation';
import { format } from 'date-fns';
import { FC } from 'react';
import strings from '../roster.translations';

interface OptimisationEvaluationCardProps {
  locationId: number;
  startDate: Date;
  endDate: Date;
  embedded?: boolean;
}

type EvaluationPanelVariant = 'latest' | 'previous';

type StatusTone = 'good' | 'warn' | 'neutral';

const CONSTRAINT_ORDER = [
  'shift_gets_worked',
  'under_covered_days',
  'manager_attendance',
  'max_shifts_per_day',
  'shift_overlap',
  'daily_max_hours',
  'mandatory_skills',
  'roster_staff_match',
  'availability_match',
  'contract_role_match',
  'shift_too_long_for_contract',
  'locked_employee',
  'consecutive_working_days',
  'weekly_max_hours',
  'weekly_min_hours',
  'optional_skills',
  'templated_employee_match',
  'monthly_min_hours',
  'consecutive_non_working_days',
  'monthly_max_hours'
] as const;

const CONSTRAINT_ORDER_MAP = new Map<string, number>(CONSTRAINT_ORDER.map((key, index) => [key, index]));

const CONSTRAINT_GROUP_OVERRIDES: Record<string, 'constraint' | 'additional_info'> = {
  shift_gets_worked: 'constraint',
  under_covered_days: 'additional_info',
  manager_attendance: 'constraint',
  max_shifts_per_day: 'constraint',
  shift_overlap: 'constraint',
  daily_max_hours: 'constraint',
  shift_too_long_for_contract: 'constraint',
  mandatory_skills: 'constraint',
  roster_staff_match: 'constraint',
  availability_match: 'constraint',
  contract_role_match: 'constraint',
  consecutive_working_days: 'constraint',
  weekly_max_hours: 'constraint',
  weekly_min_hours: 'constraint',
  optional_skills: 'constraint',
  templated_employee_match: 'constraint',
  monthly_min_hours: 'constraint',
  consecutive_non_working_days: 'constraint',
  monthly_max_hours: 'constraint',
  locked_employee: 'constraint'
};

const CONSTRAINT_CATEGORY_OVERRIDES: Record<string, 'hard' | 'soft' | 'diagnostic'> = {
  shift_gets_worked: 'soft',
  under_covered_days: 'diagnostic',
  manager_attendance: 'soft',
  max_shifts_per_day: 'hard',
  shift_overlap: 'hard',
  daily_max_hours: 'hard',
  shift_too_long_for_contract: 'hard',
  mandatory_skills: 'hard',
  roster_staff_match: 'hard',
  availability_match: 'hard',
  contract_role_match: 'hard',
  consecutive_working_days: 'hard',
  weekly_max_hours: 'hard',
  weekly_min_hours: 'soft',
  optional_skills: 'soft',
  templated_employee_match: 'soft',
  monthly_min_hours: 'soft',
  consecutive_non_working_days: 'soft',
  monthly_max_hours: 'hard',
  locked_employee: 'hard'
};

const formatSolverName = (solverType?: number) => {
  if (solverType === 1) {
    return strings.optimiseRosters.solvers.cpSat.label;
  }

  if (solverType === 0) {
    return strings.optimiseRosters.solvers.geneticAlgorithm.label;
  }

  return '-';
};

const formatPercent = (value?: number) => {
  if (value === undefined || value === null) {
    return '-';
  }

  return `${Math.round(value * 100)}%`;
};

const formatScore = (value?: number) => {
  if (value === undefined || value === null) {
    return '-';
  }

  return value.toFixed(1);
};

const formatDuration = (value?: number) => {
  if (value === undefined || value === null) {
    return '-';
  }

  return strings.optimiseRosters.evaluation.milliseconds(value);
};

const formatCalculatedAt = (value?: string) => {
  if (!value) {
    return '-';
  }

  return format(new Date(value), 'dd/MM/yyyy HH:mm');
};

const getStatusToneClasses = (tone: StatusTone) => {
  switch (tone) {
    case 'good':
      return 'border-emerald-200 bg-emerald-50 text-emerald-800';
    case 'warn':
      return 'border-amber-200 bg-amber-50 text-amber-800';
    default:
      return 'border-slate-200 bg-slate-50 text-slate-700';
  }
};

const getMetricClasses = (tone: StatusTone) => {
  switch (tone) {
    case 'good':
      return 'border-emerald-200 bg-emerald-50';
    case 'warn':
      return 'border-amber-200 bg-amber-50';
    default:
      return 'border-slate-200 bg-slate-50';
  }
};

const getRating = (score?: number) => {
  if (score === undefined || score === null) {
    return { label: strings.optimiseRosters.evaluation.noData, tone: 'neutral' as StatusTone };
  }

  if (score >= 95) {
    return { label: 'strong', tone: 'good' as StatusTone };
  }

  if (score >= 80) {
    return { label: 'fair', tone: 'warn' as StatusTone };
  }

  return { label: 'weak', tone: 'warn' as StatusTone };
};

const buildSummary = (evaluation: OptimisationEvaluationWithSummary) => {
  const summary = evaluation.summary;
  const diagnostics = evaluation.diagnostics;
  const totalShiftCount =
    summary?.totalShiftCount ?? (evaluation.assignedShiftCount ?? 0) + (evaluation.unassignedShiftCount ?? 0);
  const unfilledShiftCount = summary?.unassignedShiftCount ?? evaluation.unassignedShiftCount ?? 0;
  const dailyCoverage = diagnostics?.dailyCoverage ?? [];
  const underCoveredDays = dailyCoverage.filter(
    day => (day.assignedShiftCount ?? 0) < (day.totalShiftCount ?? 0)
  ).length;
  const constraintResults = diagnostics?.constraintResults ?? [];
  const alwaysRequiredIssues = constraintResults
    .filter(result => isConstraintResult(result) && getConstraintCategory(result) === 'hard')
    .reduce((total, result) => total + (result.issueCount ?? 0), 0);

  const coverageScore = diagnostics?.scoreBreakdown?.coverageScore ?? summary?.coverageScore;
  const assignmentScore = diagnostics?.scoreBreakdown?.assignmentScore ?? summary?.assignmentScore;
  const optimalityScore = diagnostics?.scoreBreakdown?.optimalityScore ?? summary?.optimalityScore;

  const improvementAreas = constraintResults.filter(
    result => isConstraintResult(result) && getConstraintCategory(result) === 'soft' && (result.issueCount ?? 0) > 0
  ).length;

  return {
    totalShiftCount,
    unfilledShiftCount,
    underCoveredDays,
    alwaysRequiredIssues,
    improvementAreas,
    coverageScore,
    assignmentScore,
    optimalityScore
  };
};

const getConstraintGroup = (result: OptimisationEvaluationConstraintResult) =>
  result.group ?? CONSTRAINT_GROUP_OVERRIDES[result.key] ?? 'constraint';

const getConstraintCategory = (result: OptimisationEvaluationConstraintResult) =>
  result.category ?? CONSTRAINT_CATEGORY_OVERRIDES[result.key];

const isConstraintResult = (result: OptimisationEvaluationConstraintResult) =>
  getConstraintGroup(result) === 'constraint';

const isAdditionalDiagnosticResult = (result: OptimisationEvaluationConstraintResult) =>
  getConstraintGroup(result) === 'additional_info';

const getOrderedConstraintResults = (
  evaluation: OptimisationEvaluationWithSummary,
  predicate: (result: OptimisationEvaluationConstraintResult) => boolean
) =>
  [...(evaluation.diagnostics?.constraintResults?.filter(predicate) ?? [])].sort((left, right) => {
    const leftOrder = CONSTRAINT_ORDER_MAP.get(left.key) ?? Number.MAX_SAFE_INTEGER;
    const rightOrder = CONSTRAINT_ORDER_MAP.get(right.key) ?? Number.MAX_SAFE_INTEGER;

    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }

    return left.key.localeCompare(right.key);
  });

const getConstraintTone = (result?: OptimisationEvaluationConstraintResult): StatusTone => {
  if (!result) {
    return 'neutral';
  }

  if (result.issueCount === 0) {
    return 'good';
  }

  if (getConstraintCategory(result) === 'soft' && (result.satisfactionRate ?? 0) >= 80) {
    return 'neutral';
  }

  return 'warn';
};

const formatRate = (value?: number) => {
  if (value === undefined || value === null) {
    return '-';
  }

  return `${value.toFixed(1)}%`;
};

const CONSTRAINT_EVALUATED_UNITS: Record<string, string> = {
  shift_gets_worked: 'shifts',
  under_covered_days: 'days',
  manager_attendance: 'days',
  max_shifts_per_day: 'staff-days',
  shift_overlap: 'staff-days',
  daily_max_hours: 'staff-days',
  shift_too_long_for_contract: 'assigned shifts',
  mandatory_skills: 'assigned shifts',
  roster_staff_match: 'assigned shifts',
  availability_match: 'assigned shifts',
  contract_role_match: 'assigned shifts',
  consecutive_working_days: 'staff assignments',
  weekly_max_hours: 'weekly windows',
  weekly_min_hours: 'weekly windows',
  optional_skills: 'eligible assignments',
  templated_employee_match: 'templated shifts',
  monthly_min_hours: 'staff windows',
  consecutive_non_working_days: 'staff-days',
  monthly_max_hours: 'staff windows',
  locked_employee: 'locked shifts'
};

const formatConstraintContext = (result?: OptimisationEvaluationConstraintResult) => {
  if (!result) {
    return '-';
  }

  const evaluatedCount = result.evaluatedCount ?? 0;
  const unit = CONSTRAINT_EVALUATED_UNITS[result.key] ?? 'checks';

  if (getConstraintCategory(result) === 'soft') {
    return `${result.satisfiedCount ?? 0} matched out of ${evaluatedCount} ${unit}`;
  }

  return `${result.issueCount ?? 0} issues out of ${evaluatedCount} ${unit}`;
};

const formatHoursFromSeconds = (value?: number) => {
  if (!value) {
    return undefined;
  }

  return `${(value / 3600).toFixed(1)}h`;
};

const Metric: FC<{ label: string; value: string | number; tone?: StatusTone }> = ({
  label,
  value,
  tone = 'neutral'
}) => (
  <div className={`rounded-md border px-3 py-3 ${getMetricClasses(tone)}`}>
    <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
    <div className="text-2xl font-semibold text-slate-900">{value}</div>
  </div>
);

const ConstraintRow: FC<{
  result?: OptimisationEvaluationConstraintResult;
}> = ({ result }) => {
  const tone = getConstraintTone(result);
  const label = result?.label || result?.key || '-';
  const description = result?.description;

  return (
    <div className="grid gap-2 rounded-md border border-slate-200 bg-white px-3 py-3 md:grid-cols-[minmax(0,1fr)_auto_auto] md:items-center">
      <div className="min-w-0">
        <div className="flex items-center gap-1 text-sm font-semibold text-slate-900">
          <span>{label}</span>
          {description && (
            <Tooltip
              className={BASE_TOOLTIP_CLASSES}
              triggerClassName="inline-flex items-center text-slate-400 hover:text-slate-600"
            >
              {description}
            </Tooltip>
          )}
        </div>
        <div className="text-xs text-slate-500">
          {formatConstraintContext(result)}
          {result?.deltaSeconds ? ` • ${formatHoursFromSeconds(result.deltaSeconds)}` : ''}
        </div>
      </div>
      <div
        className={`inline-flex justify-center rounded-full border px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${getStatusToneClasses(
          tone
        )}`}
      >
        {result && getConstraintCategory(result) === 'soft'
          ? formatRate(result?.satisfactionRate)
          : `${result?.issueCount ?? 0}`}
      </div>
    </div>
  );
};

const EvaluationPanel: FC<{
  evaluation: OptimisationEvaluationWithSummary;
  variant: EvaluationPanelVariant;
}> = ({ evaluation, variant }) => {
  const summary = evaluation.summary;
  const {
    totalShiftCount,
    unfilledShiftCount,
    underCoveredDays,
    alwaysRequiredIssues,
    improvementAreas,
    coverageScore,
    assignmentScore,
    optimalityScore
  } = buildSummary(evaluation);

  const coverageRating = getRating(coverageScore);
  const hardConstraintResults = getOrderedConstraintResults(
    evaluation,
    result => isConstraintResult(result) && getConstraintCategory(result) === 'hard'
  );
  const softConstraintResults = getOrderedConstraintResults(
    evaluation,
    result => isConstraintResult(result) && getConstraintCategory(result) === 'soft'
  );
  const additionalDiagnosticResults = getOrderedConstraintResults(evaluation, result =>
    isAdditionalDiagnosticResult(result)
  );

  return (
    <div className="flex flex-col gap-4 rounded-md border border-slate-200 bg-slate-50 p-4">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="text-sm font-semibold text-slate-900">
            {variant === 'latest'
              ? strings.optimiseRosters.evaluation.latestRun
              : strings.optimiseRosters.evaluation.previousRun}
          </h4>
        </div>
        <p className="text-sm text-slate-500">
          {strings.optimiseRosters.evaluation.calculatedAt}: {formatCalculatedAt(evaluation.calculatedAt)}
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <Metric label={strings.optimiseRosters.evaluation.solver} value={formatSolverName(evaluation.solverType)} />
        <Metric
          label={strings.optimiseRosters.evaluation.coverage}
          value={formatPercent(evaluation.coverageRate)}
          tone={coverageScore === undefined ? 'neutral' : coverageScore >= 95 ? 'good' : 'warn'}
        />
        <Metric
          label={strings.optimiseRosters.evaluation.solveDuration}
          value={formatDuration(summary?.solveDurationMs ?? evaluation.solveDurationMs)}
        />
        <Metric label={strings.optimiseRosters.evaluation.solverStatus} value={evaluation.solverStatus || '-'} />
        <Metric
          label={strings.optimiseRosters.evaluation.assignedShifts}
          value={`${evaluation.assignedShiftCount ?? 0}/${(evaluation.assignedShiftCount ?? 0) + (evaluation.unassignedShiftCount ?? 0)}`}
        />
        <Metric
          label={strings.optimiseRosters.evaluation.unfilledShifts}
          value={unfilledShiftCount}
          tone={unfilledShiftCount === 0 ? 'good' : 'warn'}
        />
        <Metric
          label={strings.optimiseRosters.evaluation.alwaysRequiredIssues}
          value={alwaysRequiredIssues}
          tone={alwaysRequiredIssues === 0 ? 'good' : 'warn'}
        />
        <Metric
          label={strings.optimiseRosters.evaluation.improvementAreas}
          value={improvementAreas}
          tone={improvementAreas === 0 ? 'good' : 'warn'}
        />
      </div>

      {evaluation.diagnostics?.warning && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-800">
          {evaluation.diagnostics.warning}
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="flex flex-col gap-3 rounded-md border border-slate-200 bg-white p-4">
          <div>
            <h5 className="text-sm font-semibold text-slate-900">Hard constraints</h5>
            <p className="text-sm text-slate-500">Lower issue counts are better.</p>
          </div>
          {hardConstraintResults.map(result => (
            <ConstraintRow key={result.key} result={result} />
          ))}
        </div>

        <div className="flex flex-col gap-3 rounded-md border border-slate-200 bg-white p-4">
          <div>
            <h5 className="text-sm font-semibold text-slate-900">Soft constraints</h5>
            <p className="text-sm text-slate-500">Higher satisfaction rates are better.</p>
          </div>
          {softConstraintResults.map(result => (
            <ConstraintRow key={result.key} result={result} />
          ))}
        </div>
      </div>

      {additionalDiagnosticResults.length > 0 && (
        <div className="flex flex-col gap-3 rounded-md border border-slate-200 bg-white p-4">
          <div>
            <h5 className="text-sm font-semibold text-slate-900">Additional diagnostics</h5>
            <p className="text-sm text-slate-500">
              Supplementary evaluation metrics that are not part of the GA constraint list.
            </p>
          </div>
          {additionalDiagnosticResults.map(result => (
            <ConstraintRow key={result.key} result={result} />
          ))}
        </div>
      )}
    </div>
  );
};

const OptimisationEvaluationContent: FC<{
  evaluations: OptimisationEvaluationWithSummary[];
  loading: boolean;
  error?: string;
  showHeading?: boolean;
}> = ({ evaluations, loading, error, showHeading = true }) => {
  const displayEvaluations = evaluations.length > 1 ? [...evaluations].reverse() : evaluations;

  return (
    <div className="flex flex-col gap-3">
      {showHeading ? (
        <div>
          <h3 className="text-base font-semibold text-slate-900">{strings.optimiseRosters.evaluation.title}</h3>
          <p className="text-sm text-slate-500">{strings.optimiseRosters.evaluation.compareDescription}</p>
        </div>
      ) : (
        <p className="text-sm text-slate-500">{strings.optimiseRosters.evaluation.compareDescription}</p>
      )}

      {loading && evaluations.length === 0 && (
        <p className="text-sm text-slate-500">{strings.optimiseRosters.evaluation.waiting}</p>
      )}
      {error && <p className="text-sm text-red-500">{error}</p>}
      {!loading && !error && evaluations.length === 0 && (
        <p className="text-sm text-slate-500">{strings.optimiseRosters.evaluation.noHistory}</p>
      )}

      {displayEvaluations.length > 0 && (
        <div className="grid gap-4 xl:grid-cols-2">
          {displayEvaluations.map((evaluation, index) => (
            <EvaluationPanel
              key={evaluation.optimisationEventId || `${evaluation.locationId}-${evaluation.calculatedAt}-${index}`}
              evaluation={evaluation}
              variant={displayEvaluations.length > 1 && index === 0 ? 'previous' : 'latest'}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const OptimisationEvaluationCard: FC<OptimisationEvaluationCardProps> = ({
  locationId,
  startDate,
  endDate,
  embedded = false
}) => {
  const { evaluations, loading, error } = useOptimisationEvaluationHistory(locationId, startDate, endDate, 2);

  const content = (
    <OptimisationEvaluationContent evaluations={evaluations} loading={loading} error={error} showHeading={!embedded} />
  );

  if (embedded) {
    return content;
  }

  return <TSCard className="mb-4 p-4">{content}</TSCard>;
};
